import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Button,
  Typography,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  FlowBgKindToggle,
  ImageFileDropZone,
  flowBgKindLabel
} from './flowBgAssetsUi';

const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';

function assetKind(k) {
  return k === 'slide' ? 'slide' : 'background';
}

export const BackgroundSelector = ({ open, onClose, onSelect }) => {
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listTab, setListTab] = useState('background');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadKind, setUploadKind] = useState('background');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchBackgrounds = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/backgrounds?kind=${encodeURIComponent(listTab)}`
      );
      const data = await response.json();
      setBackgrounds(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching backgrounds:', error);
      setBackgrounds([]);
    } finally {
      setLoading(false);
    }
  }, [listTab]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchBackgrounds();
  }, [open, fetchBackgrounds]);

  useEffect(() => {
    if (!createDialogOpen) return;
    setUploadKind(listTab);
    setUploadFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [createDialogOpen, listTab]);

  const handleUpload = async () => {
    const file = uploadFile || fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('kind', uploadKind);
      const response = await fetch(`${API_BASE_URL}/api/backgrounds`, {
        method: 'POST',
        body: fd
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      const created = await response.json();
      setCreateDialogOpen(false);
      setUploadFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await fetchBackgrounds();
      onSelect({
        backgroundImageId: created._id,
        backgroundImageUrl: created.imageUrl,
        backgroundThumbnailUrl: created.thumbnailUrl,
        backgroundAssetKind: assetKind(created.kind)
      });
    } catch (error) {
      console.error('Error uploading background:', error);
    } finally {
      setUploading(false);
    }
  };

  const selectPayload = (bg) => ({
    backgroundImageId: bg._id,
    backgroundImageUrl: bg.imageUrl,
    backgroundThumbnailUrl: bg.thumbnailUrl,
    backgroundAssetKind: assetKind(bg.kind)
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography component="span" variant="h6">
              Select background image
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddIcon />}
            >
              Upload image
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 280 }}>
          <Tabs
            value={listTab}
            onChange={(_, v) => {
              setListTab(v);
              setLoading(true);
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="background" label="Background" sx={{ textTransform: 'none' }} />
            <Tab value="slide" label="Slide" sx={{ textTransform: 'none' }} />
          </Tabs>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : backgrounds.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No images in this tab yet. Upload one (R2).
            </Typography>
          ) : (
            <ImageList cols={3} gap={12} sx={{ mb: 0 }}>
              {backgrounds.map((bg) => (
                <ImageListItem
                  key={bg._id}
                  sx={{ cursor: 'pointer', borderRadius: 1, overflow: 'hidden' }}
                  onClick={() => onSelect(selectPayload(bg))}
                >
                  <img
                    src={bg.thumbnailUrl || bg.imageUrl}
                    alt={flowBgKindLabel(assetKind(bg.kind))}
                    loading="lazy"
                    style={{ height: 120, objectFit: 'cover', width: '100%' }}
                  />
                  <ImageListItemBar
                    title={flowBgKindLabel(assetKind(bg.kind))}
                    subtitle={String(bg._id).slice(-8)}
                    sx={{
                      '& .MuiImageListItemBar-title': { fontSize: 12 },
                      '& .MuiImageListItemBar-subtitle': { fontSize: 10 }
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => !uploading && setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload image (R2)</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FlowBgKindToggle value={uploadKind} onChange={setUploadKind} disabled={uploading} />
            <ImageFileDropZone
              file={uploadFile}
              onFile={setUploadFile}
              disabled={uploading}
              inputRef={fileRef}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !(uploadFile || fileRef.current?.files?.[0])}
          >
            {uploading ? 'Uploading…' : 'Upload & use'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
